// Must match timing of css variable: --mini-cart-open-transition
const MINI_CART_DRAWER_ANIMATION_DURATION = 300

/**
 * Fetch Helper
 * @param {*} data : JSON
 * @returns : respone
 */
const ShopifyApiClient = async (fetchMethod = 'GET', fetchEndpoint = null, bodyJson = null, handleAsJson = true) => {
  try {
    if (!fetchEndpoint) {
      throw new Error('fetchEndpoint is required')
    }

    const fetchRes = await fetch(fetchEndpoint, {
      method: fetchMethod,
      headers: {
        'Content-Type': 'application/json',
      },
      ...(
        !bodyJson || fetchMethod === 'GET'
          ? {}
          : { body: JSON.stringify(bodyJson)}
      )
    })

    if (!fetchRes.ok) {
      throw new Error(`HTTP error! status: ${fetchRes.status}`)
    }

    const dataJson = handleAsJson
      ? await fetchRes.json()
      : await fetchRes.text()

    return dataJson
  } catch (err) {
    throw err
  }
}

// Shopify API Helpers
const shopifyApi= {
  cartGet: () => ShopifyApiClient(
    'GET',
    '/cart.js'
  ),
  cartAdd: (addItems) => ShopifyApiClient(
    'POST',
    '/cart/add.js',
    { items: Array.isArray(addItems) ? addItems : [addItems] }
  ),
  cartUpdate: (updateItemsObject) => ShopifyApiClient(
    'POST',
    '/cart/update.js',
    updateItemsObject
  ),
  sectionsRender: (sectionId) => ShopifyApiClient(
    'GET',
    `/?sections=${sectionId}`
  ),
  productRecommendations: (params) => ShopifyApiClient(
    'GET',
    `/recommendations/products?${params}`,
    null,
    false
  )
}

/**
 * WCMiniCart
 */
class WCMiniCart extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId
    this.cartOpen = false
    this.lastProductAddedId = null
    this.savedrecommendationsInnerHtml = ''
    this.cartData = {}
    this.gwpData = []
  }

  connectedCallback() {
    this.checkGwp()

    this.boundEventListener = {
      MINI_CART_ACTION: this.cartAction.bind(this),
      handleKeyDown: this.handleKeyDown.bind(this),
      handleClickAppTargetElementEvents: this.checkAppTargetElementEvents.bind(this)
    }

    window.addEventListener('MINI_CART_ACTION', this.boundEventListener.MINI_CART_ACTION)
    window.addEventListener('keydown', this.boundEventListener.handleKeyDown)
    window.addEventListener('click', this.boundEventListener.handleClickAppTargetElementEvents)


    this.checkOpenWithParams()
  }

  disconnectedCallback() {
    window.removeEventListener('MINI_CART_ACTION', this.boundEventListener.MINI_CART_ACTION)
    window.removeEventListener('keydown', this.boundEventListener.handleKeyDown)
    window.addEventListener('click', this.boundEventListener.handleClickAppTargetElementEvents)
  }

  get contentContainer() {
    return this.querySelector('[data-mini-cart-content-container]')
  }

  checkAppTargetElementEvents(event) {
    const appTargetElements = [
      '.rivo-product-action-add-to-cart.submit'
    ]

    if (appTargetElements.some(appTargetElement => event.target.closest(appTargetElement))) {
      event.preventDefault()
      event.stopPropagation()
      setTimeout(() => {
        this.openCart(event)
      }, 750)
    }
  }

  checkOpenWithParams() {
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('cart') !== 'open') { return }
    this.openCart()
    urlParams.delete('cart')
    const newParams = urlParams.toString()
    const newUrl = window.location.pathname + (newParams ? `?${newParams}` : '')
    window.history.replaceState(null, '', newUrl)
  }

  cartAction(event) {
    const actionMapping = {
      OPEN: this.cartOpen ? this.updateCart.bind(this) : this.openCart.bind(this),
      CLOSE: this.closeCart.bind(this),
      UPDATE: this.updateCart.bind(this),
    }

    const action = actionMapping[event.detail.action] ?? null
    if (action) { action(event) }
  }

  async openCart(event = null) {
    event?.preventDefault()
    this.classList.add('open')
    document.body.style.overflow = 'hidden'
    this.cartOpen = true
    if (!event.detail.disableUpdate) await this.updateCart()
    setTimeout(() => {
      this.trapFocus(true)
    }, MINI_CART_DRAWER_ANIMATION_DURATION * 1.25)
  }

  closeCart(event) {
    event.preventDefault()
    this.classList.remove('open')
    document.body.style.overflow = ''
    this.trapFocus(false)
    this.cartOpen = false
  }

  async updateCart (event = null) {
    try {
      await this.renderSection(event)
      await this.getRecommendations()
      await this.checkGwp()
      this.trapFocus(this.cartOpen)
      document.querySelectorAll('[data-cart-count]')
        .forEach((el) => el.innerHTML = this.cartData.item_count)
    } catch (err) {
      console.error(err)
    }
  }

  handleKeyDown (event) {
    if (event.key === 'Escape') {
      this.closeCart(event)
    }
  }

  updateCartData = async () => {
    try {
      const cartDataScriptElm = this.querySelector('[data-wc-mini-cart-json]')
      this.cartData = JSON.parse(cartDataScriptElm?.textContent ?? '{}')
      return this.cartData
    } catch (err) {
      console.error(err)
      return {}
    }
  }

  updateGwpData = async () => {
    const gwpDataScriptElm = this.querySelector('[data-wc-mini-cart-gwp-json]')
    this.gwpData = JSON.parse(gwpDataScriptElm?.textContent ?? '[]')
    return this.gwpData
  }

  async gwpRemoveItems (cartSubtotalDollars = 0) {
    const gwpItemsToRemove = (this.cartData?.items ?? [])
      .filter(item => {
        const gwpItem = this.gwpData.find(gwp =>
            gwp.gwp_variant_id === item.variant_id
        ) ?? null

        const hasGwpProperty = (
          typeof item.properties?._gwp_item !== 'undefined' ||
          item.properties?._source === 'Rebuy'
        )

        return (
          hasGwpProperty && (
            gwpItem === null ||
            cartSubtotalDollars < gwpItem?.cart_subtotal_threshold
          )
        )
      })
      .reduce((removeObj, item) => {
        removeObj[item.key] = 0
        return removeObj
      }, {})


    if (Object.keys(gwpItemsToRemove).length === 0) { return null }
    return await shopifyApi.cartUpdate({
      updates: gwpItemsToRemove,
      sections: this.sectionId
    })
  }

  async gwpAddItems (cartSubtotalDollars = 0) {
    const itemsToAdd = (this.gwpData ?? [])
      .filter(gwpItem => {
        const isGwpItemInCart = this.cartData?.items?.some(cartItem =>
          gwpItem.gwp_variant_id === cartItem.variant_id &&
          cartItem.properties?._gwp_item === 'automatic'
        ) ?? true

        return (
          gwpItem.gwp_method === 'automatic' &&
          cartSubtotalDollars >= gwpItem?.cart_subtotal_threshold &&
          !isGwpItemInCart
        )
      })
      .map(item => ({
        id: item.gwp_variant_id,
        quantity: 1,
        properties: {
          _gwp_item: 'automatic',
        }
      }))

      if (itemsToAdd.length === 0) { return null }
      return await shopifyApi.cartAdd(itemsToAdd)
  }

  async checkGwp () {
    this.updateCartData()
    this.updateGwpData()
    const cartSubtotalDollars = (this.cartData?.total_price ?? 0) / 100
    const gwpRemoveRes = await this.gwpRemoveItems(cartSubtotalDollars)
    const gwpAddRes = await this.gwpAddItems(cartSubtotalDollars)
    if (!gwpRemoveRes && !gwpAddRes) { return }
    await this.updateCart({
      details: {
        cartResponseJson: gwpRemoveRes ?? gwpAddRes
      }
    })
  }

  async renderSection (event = null) {
    const passedSectionHtmlString = event?.detail
      ?.cartResponseJson
      ?.sections
      ?.[this.sectionId] ?? null

    const sectionHtmlString = passedSectionHtmlString === null
      ? await this.fetchSectionHtmlString()
      : passedSectionHtmlString

    const lastFocusedElementId = document.activeElement?.id ?? null
    const lastContentContainerScrollPos = this.contentContainer?.scrollTop ?? 0

    const renderInjectionElements = [...this.querySelectorAll('[data-render-inject]')]
    this.innerHTML = this.getElementInnerHtmlFromSectionString(
      sectionHtmlString,
      `[data-section-id="${this.sectionId}"]`,
      renderInjectionElements
    )

    setTimeout(() => {
      const newContentContainerElement = this.contentContainer
      if (newContentContainerElement) {
        newContentContainerElement.scrollTop = lastContentContainerScrollPos
      }

      if (lastFocusedElementId) {
        const newFocusedElement = document.getElementById(lastFocusedElementId)
        newFocusedElement?.focus()
      }
    }, 10)
  }

  async fetchSectionHtmlString() {
    const sectionData = await shopifyApi.sectionsRender(this.sectionId)
    const sectionHtmlString = sectionData[this.sectionId] ?? null
    return sectionHtmlString
  }

  async getRecommendations() {
    const recommendationsTarget = '[data-mini-cart-recommendations]'
    const recommendationsElm = this.querySelector(recommendationsTarget)

    if (!recommendationsElm) { return }

    const lastProductAddedId = this.querySelector('[data-mini-cart-item]')?.dataset?.productId ?? null
    if (!lastProductAddedId) {
      window.yotpoWidgetsContainer.initWidgets()
      return
    }
    if (lastProductAddedId === this.lastProductAddedId) {
      recommendationsElm.innerHTML = this.savedrecommendationsInnerHtml
      window.yotpoWidgetsContainer.initWidgets()
      return
    }

    this.lastProductAddedId = lastProductAddedId
    const params = new URLSearchParams();
    params.set('product_id', lastProductAddedId);
    params.set('limit', recommendationsElm.dataset.maxRecommendationsCount ?? 4);
    params.set('intent', recommendationsElm.dataset.recommendationsIntent ?? 'related');
    params.set('section_id', this.sectionId);

    const recommendationsResponseText = await shopifyApi.productRecommendations(params.toString())
    recommendationsElm.innerHTML = this.getElementInnerHtmlFromSectionString(
      recommendationsResponseText,
      recommendationsTarget
    )
    this.savedrecommendationsInnerHtml = recommendationsElm.innerHTML

    setTimeout(() => {
      window.yotpoWidgetsContainer.initWidgets()
    }, 100)
  }

  getElementInnerHtmlFromSectionString (
    sectionHtmlString = '',
    targetSelector = null,
    renderInjectionElements = []
  ) {
    if (!sectionHtmlString || !targetSelector) { return '' }
    const parser = new DOMParser()
    const sectionHtml = parser.parseFromString(sectionHtmlString, 'text/html')

    renderInjectionElements.forEach((elmToInject) => {
      const targetQuery = `[data-render-inject="${elmToInject.dataset.renderInject}"]`
      const renderInjectionTarget = sectionHtml.querySelector(targetQuery)
      renderInjectionTarget?.replaceWith(elmToInject.cloneNode(true))
    })

    const sectionTargetElement = sectionHtml.querySelector(targetSelector)
    return sectionTargetElement?.innerHTML ?? ''
  }

  trapFocus(isTrapped) {
    const focusableElements = this.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    const firstFocusableElement = focusableElements[0]
    const lastFocusableElement = focusableElements[focusableElements.length - 1]

    const handleTab = (event) => {
      if (event.key !== 'Tab') { return }
      const isFirst = (document.activeElement === firstFocusableElement)
      const isLast = (document.activeElement === lastFocusableElement)
      if ((event.shiftKey && isFirst) || (!event.shiftKey && isLast)) {
        (event.shiftKey ? lastFocusableElement : firstFocusableElement).focus();
        event.preventDefault();
      }
    }

    if (isTrapped === true) {
      this.addEventListener('keydown', handleTab)
    } else {
      this.removeEventListener('keydown', this.handleTab)
    }
  }
}

if (typeof customElements.get('wc-mini-cart') === 'undefined') {
  customElements.define('wc-mini-cart', WCMiniCart);
}

/**
 * WCMiniCartItem
 */
class WCMiniCartAtcButton extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.boundATCEventListener = this.addToCart.bind(this)
    this.addEventListener('click', this.boundATCEventListener)
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.boundATCEventListener)
  }

  async addToCart(event) {
    event.preventDefault()

    const atcData = {
      quantity: 1,
      id: this.dataset.variantId,
      properties: this.dataset.gwp
        ? { '_gwp_item': this.dataset?.gwp ?? null }
        : {},
      sections: this.dataset.sectionId ?? ''
    }

    const atcJson = await shopifyApi.cartAdd(atcData)

    window.dispatchEvent(new CustomEvent('MINI_CART_ACTION', {
      detail: {
        action: 'UPDATE',
        cartResponseJson: atcJson
      }
    }))
  }
}

if (typeof customElements.get('wc-mini-cart-atc-button') === 'undefined') {
  customElements.define('wc-mini-cart-atc-button', WCMiniCartAtcButton);
}
